import React, { useState, useEffect, useCallback } from 'react';
import InnCard from './InnCard';
import MoleculeImage from './MoleculeImage';
import StructureEditor from './StructureEditor';
import API_BASE from '../config';
import './InnBrowse.css';

const InnBrowse = () => {
  const [mode, setMode] = useState('browse'); // 'browse' | 'structure'

  // Browse state
  const [search, setSearch]           = useState('');
  const [field, setField]             = useState('all');
  const [hasStructure, setHasStructure] = useState('yes');
  const [listNumber, setListNumber]   = useState('');
  const [page, setPage]               = useState(1);
  const [compounds, setCompounds]     = useState([]);
  const [total, setTotal]             = useState(0);
  const [pages, setPages]             = useState(1);
  const [loading, setLoading]         = useState(false);

  // Structure search state
  const [querySmiles, setQuerySmiles] = useState('');
  const [showEditor, setShowEditor]   = useState(false);
  const [searchType, setSearchType]   = useState('substructure');
  const [threshold, setThreshold]     = useState(0.7);
  const [structResults, setStructResults] = useState([]);
  const [structLoading, setStructLoading] = useState(false);
  const [structSearched, setStructSearched] = useState(false);

  const [error, setError] = useState(null);

  const PER_PAGE = 24;

  const fetchBrowse = useCallback(async (p = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: p,
        per_page: PER_PAGE,
        search,
        field,
      });
      if (hasStructure) params.set('has_structure', hasStructure);
      const ln = listNumber.replace(/\D/g, '');  // strip "PL-" prefix if typed
      if (ln) params.set('list_number', ln);
      const resp = await fetch(`${API_BASE}/inn?${params}`);
      const data = await resp.json();
      setCompounds(data.compounds || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
      setPage(p);
    } catch (err) {
      setError('Failed to load: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [search, field, hasStructure, listNumber]);

  useEffect(() => {
    if (mode === 'browse') fetchBrowse(1);
  }, [mode, fetchBrowse]);

  const handleBrowseSubmit = (e) => {
    e.preventDefault();
    // If the user typed something like "PL-80" or "pl80" in the main search box,
    // treat it as a list number filter automatically
    const plMatch = search.match(/^pl-?(\d+)$/i);
    if (plMatch) {
      setListNumber(plMatch[1]);
      setSearch('');
      return; // the state change will trigger fetchBrowse via useEffect
    }
    fetchBrowse(1);
  };

  const handleStructureSearch = async () => {
    if (!querySmiles) { setError('Draw a structure first'); return; }
    setStructLoading(true);
    setError(null);
    setStructSearched(true);
    try {
      const endpoint = searchType === 'substructure'
        ? `${API_BASE}/inn/search/substructure`
        : `${API_BASE}/inn/search/similarity`;
      const body = { smiles: querySmiles };
      if (searchType === 'similarity') body.threshold = threshold;
      const resp = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Search failed');
      setStructResults(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setStructLoading(false);
    }
  };

  const switchMode = (m) => {
    setMode(m);
    setError(null);
    setStructSearched(false);
    setStructResults([]);
  };

  return (
    <div className="inn-browse-container">
      <div className="inn-browse-header">
        <h1>WHO INN Database</h1>
        <span className="inn-subtitle">International Nonproprietary Names · PL-1 to PL-134 · {total > 0 && mode === 'browse' ? `${total} compounds` : ''}</span>
      </div>

      <div className="inn-mode-toggle">
        <button className={`mode-btn ${mode === 'browse' ? 'active' : ''}`} onClick={() => switchMode('browse')}>
          Browse / Text Search
        </button>
        <button className={`mode-btn ${mode === 'structure' ? 'active' : ''}`} onClick={() => switchMode('structure')}>
          Structure Search
        </button>
      </div>

      {/* Browse mode */}
      {mode === 'browse' && (
        <form onSubmit={handleBrowseSubmit} className="inn-search-bar">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search INN names, drug class, CAS number..."
            className="inn-search-input"
          />
          <select value={field} onChange={e => setField(e.target.value)} className="inn-select">
            <option value="all">All fields</option>
            <option value="inn">INN name</option>
            <option value="description">Drug class</option>
            <option value="cas">CAS number</option>
          </select>
          <select value={hasStructure} onChange={e => setHasStructure(e.target.value)} className="inn-select">
            <option value="">All compounds</option>
            <option value="yes">Has structure</option>
            <option value="no">Biologics only</option>
          </select>
          <input
            type="text"
            value={listNumber}
            onChange={e => setListNumber(e.target.value)}
            placeholder="PL-133"
            className="inn-pl-input"
          />
          <button type="submit" className="inn-search-btn" disabled={loading}>
            {loading ? 'Loading…' : 'Search'}
          </button>
        </form>
      )}

      {/* Structure search mode */}
      {mode === 'structure' && (
        <div className="inn-structure-controls">
          <div className="struct-draw-area">
            <button className="draw-button" onClick={() => setShowEditor(true)}>
              {querySmiles ? 'Redraw Structure' : 'Draw Structure'}
            </button>
            {querySmiles && (
              <div className="query-preview">
                <MoleculeImage smiles={querySmiles} width={160} height={140} />
              </div>
            )}
          </div>
          <div className="struct-options">
            <div className="struct-type-row">
              <label>
                <input type="radio" value="substructure" checked={searchType === 'substructure'}
                  onChange={() => setSearchType('substructure')} /> Substructure
              </label>
              <label>
                <input type="radio" value="similarity" checked={searchType === 'similarity'}
                  onChange={() => setSearchType('similarity')} /> Similarity
              </label>
            </div>
            {searchType === 'similarity' && (
              <div className="threshold-row">
                <label>Threshold: {(threshold * 100).toFixed(0)}%</label>
                <input type="range" min="0.3" max="1.0" step="0.05"
                  value={threshold} onChange={e => setThreshold(parseFloat(e.target.value))} />
              </div>
            )}
            <button className="inn-search-btn" onClick={handleStructureSearch}
              disabled={structLoading || !querySmiles}>
              {structLoading ? 'Searching…' : 'Search'}
            </button>
            {structSearched && (
              <button className="inn-clear-btn" onClick={() => { setStructSearched(false); setStructResults([]); setQuerySmiles(''); }}>
                Clear
              </button>
            )}
          </div>
        </div>
      )}

      {error && <div className="inn-error">{error}</div>}

      {/* Browse results */}
      {mode === 'browse' && !loading && (
        <>
          <div className="inn-results-header">
            <span>{total} compound{total !== 1 ? 's' : ''}</span>
            {pages > 1 && (
              <div className="inn-pagination">
                <button onClick={() => fetchBrowse(page - 1)} disabled={page <= 1}>‹ Prev</button>
                <span>Page {page} of {pages}</span>
                <button onClick={() => fetchBrowse(page + 1)} disabled={page >= pages}>Next ›</button>
              </div>
            )}
          </div>
          <div className="inn-grid">
            {compounds.map(c => <InnCard key={c.id} compound={c} />)}
          </div>
          {pages > 1 && (
            <div className="inn-pagination inn-pagination-bottom">
              <button onClick={() => fetchBrowse(page - 1)} disabled={page <= 1}>‹ Prev</button>
              <span>Page {page} of {pages}</span>
              <button onClick={() => fetchBrowse(page + 1)} disabled={page >= pages}>Next ›</button>
            </div>
          )}
        </>
      )}

      {/* Structure search results */}
      {mode === 'structure' && structSearched && !structLoading && (
        <div className="inn-struct-results">
          <h2>{structResults.length} compound{structResults.length !== 1 ? 's' : ''} found</h2>
          {structResults.length > 0 ? (
            <div className="inn-grid">
              {structResults.map(c => <InnCard key={c.id} compound={c} />)}
            </div>
          ) : (
            <p className="inn-no-results">
              No matches. {searchType === 'similarity' ? 'Try lowering the threshold.' : 'Try a simpler substructure.'}
            </p>
          )}
        </div>
      )}

      {showEditor && (
        <StructureEditor
          smiles={querySmiles}
          onSave={(s) => { setQuerySmiles(s); setShowEditor(false); }}
          onClose={() => setShowEditor(false)}
        />
      )}
    </div>
  );
};

export default InnBrowse;
